import type { AppDispatch } from '@appStore/store'

import { automationApi, Tag } from 'api/automationApi'
import camelCase from 'lodash/camelCase'
import { appConnector } from 'shared/connector'
import { fetchViaExtensionConditional } from 'shared/connector/rtk-adapt'

import type * as Api from './model/types'

import {
	addConversationCursor,
	saveConversation,
	saveMessage
} from './model/stores'
import { updateReactions } from './utils/optimistic'
import { fetchAttachments } from './utils/query'

type CamelCase<S extends string> =
	S extends `${infer P1}-${infer P2}${infer P3}`
		? `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}`
		: Lowercase<S>

type GetProps = Api.GetProps & { cursor?: string }

function getConnectorConversations({ filter, search }: Api.GetProps) {
	if (search) {
		return appConnector.linkedIn.mailboxes.search
	}

	const mapped = filter === 'primary' ? 'inbox' : camelCase(filter)
	const fixedType = mapped as CamelCase<Api.Filter>

	// @ts-expect-error dynamic access to connector mailboxes
	return appConnector.linkedIn.mailboxes[fixedType]
}

export const api = automationApi.injectEndpoints({
	endpoints: create => ({
		archive: create.mutation<void, Api.UpdateConversation>({
			invalidatesTags: [Tag.Conversation],
			queryFn: fetchViaExtensionConditional<void, Api.UpdateConversation>(
				({ id, mark = true }) => ({
					fetchWithConnector() {
						return mark
							? appConnector.linkedIn.conversations.archiveConversation(id)
							: appConnector.linkedIn.conversations.unarchiveConversation(id)
					},
					method: 'PUT',

					url: `conversations/${id}/${mark ? 'archive' : 'unarchive'}`
				})
			)
		}),
		attachment: create.query<string, Api.AttachmentProps>({
			queryFn: fetchAttachments
		}),

		conversations: create.query<Api.ConversationResponse, GetProps>({
			providesTags: [Tag.Conversation],
			async queryFn({ cursor, filter, mailboxFilter, search }, storeUtils, ...args) {
				const fetch = fetchViaExtensionConditional<
					Api.ConversationResponse,
					GetProps
				>(({ cursor, filter, mailboxFilter, search }) => {
					const isMailbox = filter === 'focused' || filter === 'primary' || filter === 'other'
					const mailboxKey = filter === 'primary' ? 'inbox' : filter
					const type = search ? 'search' : isMailbox ? mailboxKey : filter
					const end = cursor === '' ? 'recent' : 'next'

					function getMailboxTypeForConnector() {
						if (mailboxKey === 'focused') return 'Focused'
						if (mailboxKey === 'inbox') return 'Inbox'
						return 'Other'
					}

					function getFilterParamValue(sub?: Api.GetProps['mailboxFilter']) {
						if (!sub) return undefined
						switch (sub) {
							case 'in-mail':
								return 'InMail'
							case 'myConnections':
								return 'myConnections'
							case 'starred':
								return 'Starred'
							case 'unread':
								return 'Unread'
							default:
								return undefined
						}
					}

					return {
						fetchWithConnector() {
							if (search) {
								const conversation = getConnectorConversations({ filter, mailboxFilter, search })
								if (cursor === '') {
									return conversation.getRecentConversations(search || undefined)
								}
								return conversation.getNextConversations(search || undefined, cursor)
							}

							if (isMailbox && mailboxFilter) {
								const mailboxType = getMailboxTypeForConnector()
								switch (mailboxFilter) {
									case 'in-mail':
										return cursor === ''
											? appConnector.linkedIn.mailboxes.inMail.getRecentConversations(mailboxType)
											: appConnector.linkedIn.mailboxes.inMail.getNextConversations(mailboxType, cursor)
									case 'myConnections':
										return cursor === ''
											? appConnector.linkedIn.mailboxes.myConnections.getRecentConversations(mailboxType)
											: appConnector.linkedIn.mailboxes.myConnections.getNextConversations(mailboxType, cursor)
									case 'starred':
										return cursor === ''
											? appConnector.linkedIn.mailboxes.starred.getRecentConversations(mailboxType)
											: appConnector.linkedIn.mailboxes.starred.getNextConversations(mailboxType, cursor)
									case 'unread':
										return cursor === ''
											? appConnector.linkedIn.mailboxes.unread.getRecentConversations(mailboxType)
											: appConnector.linkedIn.mailboxes.unread.getNextConversations(mailboxType, cursor)
								}
							}

							const conversation = getConnectorConversations({ filter, mailboxFilter, search })
							if (!cursor) {
								return conversation.getRecentConversations()
							}
							return conversation.getNextConversations(cursor)
						},
						params: {
							filter: isMailbox ? getFilterParamValue(mailboxFilter) : undefined,
							keywords: search || undefined,
							nextCursor: cursor === '' ? undefined : cursor
						},
						url: `mailboxes/${type}/conversations/${end}`
					}
				})

				const response = await fetch(
					{ cursor, filter, mailboxFilter, search },
					storeUtils,
					...args
				)

				if (response.data !== undefined) {
					updateConversations(response.data.items, storeUtils.dispatch)
					addConversationCursor(response.data.nextCursor)
				}

				return response
			}
		}),

		delete: create.mutation<void, string>({
			invalidatesTags: [Tag.Conversation],
			queryFn: fetchViaExtensionConditional<void, string>(id => ({
				fetchWithConnector: () =>
					appConnector.linkedIn.conversations.deleteConversation(id),
				method: 'DELETE',
				url: `conversations/${id}`
			}))
		}),
		deleteMessage: create.mutation<void, Api.DeleteMessageBody>({
			invalidatesTags: [Tag.Messages],
			queryFn: fetchViaExtensionConditional<void, Api.DeleteMessageBody>(
				({ conversation, id }) => ({
					fetchWithConnector() {
						return appConnector.linkedIn.conversations.deleteConversationEvent(
							conversation,
							id
						)
					},
					method: 'DELETE',
					url: `conversations/${conversation}/events/${id}`
				})
			)
		}),
		editMessage: create.mutation<void, Api.EditMessageBody>({
			invalidatesTags: [Tag.Messages, Tag.Conversation],
			queryFn: fetchViaExtensionConditional<void, Api.EditMessageBody>(
				({ conversation, id, text }) => ({
					data: {
						text
					},
					fetchWithConnector() {
						return appConnector.linkedIn.conversations.changeConversationEvent(
							conversation,
							id,
							text
						)
					},
					headers: {
						/**
						 * ! Don't delete
						 * RTK Query sends plain string as FormData if Content-Type is not set
						 *  */
						'Content-Type': 'application/json'
					},
					method: 'PUT',
					url: `conversations/${conversation}/events/${id}`
				})
			)
		}),
		markRead: create.mutation<void, Api.UpdateConversation>({
			invalidatesTags: [Tag.Conversation, Tag.UnreadStats],

			queryFn: fetchViaExtensionConditional<void, Api.UpdateConversation>(
				({ id, mark = true }) => ({
					fetchWithConnector() {
						return mark
							? appConnector.linkedIn.conversations.readConversation(id)
							: appConnector.linkedIn.conversations.unreadConversation(id)
					},
					method: 'PUT',
					url: `conversations/${id}/${mark ? 'read' : 'unread'}`
				})
			)
		}),
		messages: create.query<Api.EventsResponse, string>({
			providesTags: [Tag.Messages],
			async queryFn(...props) {
				const fetch = fetchViaExtensionConditional<Api.EventsResponse, string>(
					conversation => ({
						fetchWithConnector: () =>
							appConnector.linkedIn.conversations.getConversationRecentEvents(
								conversation
							),
						method: 'GET',
						url: `conversations/${conversation}/events/recent`
					})
				)

				const response = await fetch(...props)

				if (response.data !== undefined) {
					response.data.items.forEach(saveMessage)
				}

				return response
			}
		}),
		messagesPrev: create.query<Api.EventsResponse, Api.PrevMessageProps>({
			providesTags: [Tag.Messages],
			async queryFn(...props) {
				const fetch = fetchViaExtensionConditional<
					Api.EventsResponse,
					Api.PrevMessageProps
				>(({ id, prevCursor }) => ({
					fetchWithConnector: () =>
						appConnector.linkedIn.conversations.getConversationNextEvents(
							id,
							prevCursor
						),
					method: 'GET',
					params: {
						prevCursor
					},
					url: `conversations/${id}/events/next`
				}))

				const response = await fetch(...props)

				if (response.data !== undefined) {
					response.data.items.forEach(saveMessage)
				}

				return response
			}
		}),
		participants: create.query<Api.Participant[], string>({
			queryFn: fetchViaExtensionConditional<Api.Participant[], string>(
				conversation => ({
					fetchWithConnector: () =>
						appConnector.linkedIn.conversations.getConversationParticipants(
							conversation
						),
					method: 'GET',
					url: `conversations/${conversation}/participants`
				})
			)
		}),
		reaction: create.mutation<void, Api.ReactionBody>({
			invalidatesTags: [Tag.Messages],
			async queryFn(props, storeUtils, ...args) {
				const cancel = updateReactions(props)
				const fetch = fetchViaExtensionConditional<void, Api.ReactionBody>(
					({ conversation, emoji, mark, message }) => ({
						fetchWithConnector() {
							const method = mark
								? ('addConversationEventReaction' as const)
								: ('revokeConversationEventReaction' as const)

							return appConnector.linkedIn.conversations[method](
								conversation,
								message,
								emoji
							)
						},
						method: mark ? 'POST' : 'DELETE',
						url: `conversations/${conversation}/events/${message}/reactions/${emoji}`
					})
				)

				const response = await fetch(props, storeUtils, ...args)

				if (response.error !== undefined) {
					cancel?.()
				}

				return response
			}
		}),
		seen: create.query<Api.SeenResponse[], string>({
			providesTags: [Tag.Conversation],
			queryFn: fetchViaExtensionConditional<Api.SeenResponse[], string>(
				conversation => ({
					fetchWithConnector: () =>
						appConnector.linkedIn.conversations.getConversationSeenReceipts(
							conversation
						),
					method: 'GET',
					url: `conversations/${conversation}/seen-receipts`
				})
			)
		}),
		sendMessage: create.mutation<void, Api.NewMessageBody>({
			invalidatesTags: [Tag.Messages, Tag.Conversation],

			queryFn: fetchViaExtensionConditional<void, Api.NewMessageBody>(
				({ conversation, ...data }) => ({
					data,
					fetchWithConnector: () =>
						appConnector.linkedIn.conversations.createConversationEvent(
							conversation,
							data.text,
							data.attachments,
							data.reply_to
						),
					method: 'POST',
					url: `conversations/${conversation}/events`
				})
			)
		}),
		setFocus: create.mutation<void, string>({
			invalidatesTags: [Tag.Conversation],
			queryFn: fetchViaExtensionConditional<void, string>(id => ({
				fetchWithConnector: () =>
					appConnector.linkedIn.conversations.moveConversationToFocusedMailbox(
						id
					),
				method: 'PUT',
				url: `conversations/${id}/focused`
			}))
		}),
		setOther: create.mutation<void, string>({
			invalidatesTags: [Tag.Conversation],
			queryFn: fetchViaExtensionConditional<void, string>(id => ({
				fetchWithConnector: () =>
					appConnector.linkedIn.conversations.moveConversationToOtherMailbox(
						id
					),
				method: 'PUT',
				url: `conversations/${id}/other`
			}))
		}),
		settings: create.query<{ focusedInboxEnabled: boolean }, void>({
			queryFn: fetchViaExtensionConditional<
				{ focusedInboxEnabled: boolean },
				void
			>(() => ({
				fetchWithConnector: () => appConnector.linkedIn.getSettings(),
				method: 'GET',
				url: 'settings'
			}))
		}),
		star: create.mutation<void, Api.UpdateConversation>({
			queryFn: fetchViaExtensionConditional<void, Api.UpdateConversation>(
				({ id, mark }) => ({
					fetchWithConnector: () =>
						mark
							? appConnector.linkedIn.conversations.starConversation(id)
							: appConnector.linkedIn.conversations.unstarConversation(id),
					method: 'PUT',
					url: `conversations/${id}/${mark ? 'star' : 'unstar'}`
				})
			)
		}),
		sync: create.mutation<string, any>({
			query: id => ({
				method: 'POST',
				url: `conversations/${id}/sync`
			})
		}),
		unread: create.query<Api.UnreadStats, void>({
			providesTags: [Tag.UnreadStats],
			queryFn: fetchViaExtensionConditional<Api.UnreadStats, void>(() => ({
				fetchWithConnector: () =>
					appConnector.linkedIn.messaging.getUnreadConversationsCounters(),
				method: 'GET',
				url: 'messaging/unread-conversations-counters'
			}))
		})
	}),
	overrideExisting: true
})

function updateConversations(
	conversations: Api.Conversation[],
	dispatch: AppDispatch
) {
	conversations.forEach(saveConversation)

	const updates: {
		arg: string
		endpointName: 'participants'
		value: Api.Participant[]
	}[] = []

	for (const conversation of conversations) {
		saveConversation(conversation)
		updates.push({
			arg: conversation.entity_id,
			endpointName: 'participants',
			value: conversation.participants
		})
	}

	const action = api.util.upsertQueryEntries(updates)
	dispatch(action)
}
